import { useRef, useCallback } from "react";

/**
 * Custom hook for managing scroll-to-bottom functionality and detecting user scroll state
 * @param {Object} options - Configuration options
 * @param {boolean} options.userHasScrolledUp - Whether user has manually scrolled up from bottom
 * @param {Function} options.setUserHasScrolledUp - Function to set user has scrolled up
 * @param {number} [options.threshold=100] - Distance in pixels from bottom to consider "near bottom"
 * @returns {Object} Hook interface
 * @returns {React.MutableRefObject} scrollContainerRef - Ref to attach to the scrollable container
 * @returns {Function} isNearBottom - Function to check if user is near bottom
 * @returns {Function} scrollToBottom - Function to scroll container to bottom
 * @returns {Function} handleScroll - Scroll event handler to attach to container
 */
export const useScrollToBottom = ({
  threshold = 100,
  userHasScrolledUp,
  setUserHasScrolledUp,
} = {}) => {
  const scrollContainerRef = useRef(null);

  /**
   * Scrolls the container to the bottom
   */
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, []);

  /**
   * Check if user is near the bottom of the scroll container
   * @returns {boolean} True if user is near bottom, false otherwise
   */
  const isNearBottom = useCallback(() => {
    if (!scrollContainerRef.current) {
      return true;
    }

    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, [threshold]);

  /**
   * Handle scroll events to detect if user has scrolled up
   */
  const handleScroll = useCallback(() => {
    setUserHasScrolledUp(!isNearBottom());
  }, [isNearBottom, setUserHasScrolledUp]);

  return {
    scrollContainerRef,
    scrollToBottom,
    isNearBottom,
    userHasScrolledUp,
    handleScroll,
    setUserHasScrolledUp, // Exposed for manual state control if needed
  };
};
